import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, Briefcase, Download, Loader2, RefreshCw, ShieldCheck, TrendingUp,
} from 'lucide-react';
import PageHeader from '../../../components/layout/PageHeader';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { authHeaders } from '../../../lib/apiAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Book View — boutique pilot pack frontend.
 *
 * One page that shows an institutional tenant their entire book through
 * the committee's eyes:
 *   - aggregated NAV + concentration (top1/top3/top5/HHI)
 *   - sector + size breakdown
 *   - committee verdict mix
 *   - "disagreement" list (positions the committee wants to TRIM/SELL)
 *   - full holdings matrix with one-click trade-ticket PDF download
 */

function fmtINR(n) {
  if (n === null || n === undefined) return '—';
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${Number(n).toLocaleString('en-IN')}`;
}

function fmtPct(n, digits = 2) {
  if (n === null || n === undefined) return '—';
  return `${Number(n).toFixed(digits)}%`;
}

const VERDICT_COLORS = {
  BUY: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  ADD: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  HOLD: 'bg-slate-100 text-slate-700 border-slate-300',
  TRIM: 'bg-amber-100 text-amber-800 border-amber-300',
  SELL: 'bg-rose-100 text-rose-800 border-rose-300',
  PASS: 'bg-zinc-100 text-zinc-700 border-zinc-300',
};

function VerdictBadge({ verdict }) {
  if (!verdict) return <span className="text-xs text-muted-foreground">—</span>;
  const cls = VERDICT_COLORS[verdict] || 'bg-slate-100 text-slate-700';
  return <span className={`inline-block rounded border px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{verdict}</span>;
}

function StatCard({ label, value, hint, icon: Icon, accent = 'text-foreground' }) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className={`mt-1 text-2xl font-bold ${accent}`}>{value}</div>
            {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
          </div>
          {Icon && <Icon className="h-5 w-5 text-muted-foreground" />}
        </div>
      </CardContent>
    </Card>
  );
}

function HorizontalBar({ pct, color = 'bg-blue-500' }) {
  const w = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className="h-1.5 w-full rounded bg-slate-100">
      <div className={`h-1.5 rounded ${color}`} style={{ width: `${w}%` }} />
    </div>
  );
}

export default function BookViewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busyTicket, setBusyTicket] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const headers = await authHeaders();
      const res = await axios.get(`${API}/advisor-books/risk`, { headers });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load book risk overlay');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadTicket = useCallback(async (isin, companyName) => {
    setBusyTicket(isin);
    try {
      const headers = await authHeaders();
      // We need the latest decision_id for this ISIN. The risk overlay
      // doesn't return it directly, so query the decisions list.
      const dec = await axios.get(`${API}/committee/decisions`, {
        headers, params: { isin, limit: 1 },
      });
      const decisionId = dec?.data?.items?.[0]?.id;
      if (!decisionId) {
        toast.warning(`No committee decision yet for ${companyName}.`, {
          description: 'Run the committee on this name first to generate a trade ticket.',
        });
        return;
      }
      const url = `${API}/committee/decisions/${decisionId}/ticket.pdf`;
      const resp = await axios.get(url, { headers, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `ticket_${isin}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (e) {
      toast.error('Failed to download trade ticket', {
        description: e?.response?.data?.detail || e.message || 'Please try again.',
      });
    } finally {
      setBusyTicket(null);
    }
  }, []);

  const triggerRun = useCallback(async () => {
    setRunning(true);
    try {
      const headers = await authHeaders();
      await axios.post(`${API}/advisor-books/run-now`, {}, { headers });
      toast.success('Committee batch triggered', {
        description: 'Verdicts over your books will appear in a few minutes.',
      });
    } catch (e) {
      toast.error('Failed to trigger committee batch', {
        description: e?.response?.data?.detail || e.message || 'Please try again.',
      });
    } finally {
      setRunning(false);
    }
  }, []);

  const sectorMax = useMemo(
    () => Math.max(1, ...(data?.sector_breakdown || []).map((s) => s.weight_pct)),
    [data],
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="space-y-4">
        <PageHeader title="Book view" subtitle="Boutique pilot pack" icon={Briefcase} />
        <Card>
          <CardContent className="py-6 text-sm text-rose-600">
            {err}
          </CardContent>
        </Card>
      </div>
    );
  }

  const t = data?.totals || {};
  const c = data?.concentration || {};
  const v = data?.verdict_breakdown || {};
  const s = data?.size_breakdown || {};
  const holdings = data?.holdings || [];
  const disagreement = data?.disagreement || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Book view"
        subtitle="Your aggregated client books through the committee's eyes"
        icon={Briefcase}
        actions={(
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load}>
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
            <Button size="sm" onClick={triggerRun} disabled={running}>
              {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <TrendingUp className="mr-2 h-4 w-4" />}
              Run committee on book
            </Button>
          </div>
        )}
      />

      {/* Top stats */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Aggregated NAV" value={fmtINR(t.nav_inr)}
          hint={`${t.book_count || 0} active book(s) · ${t.holding_count || 0} holdings`}
          icon={Briefcase} />
        <StatCard label="Top-1 concentration" value={fmtPct(c.top1_pct)} icon={AlertTriangle}
          accent={c.top1_pct >= 10 ? 'text-amber-600' : 'text-foreground'} />
        <StatCard label="Top-5 concentration" value={fmtPct(c.top5_pct)}
          hint={`HHI ${c.hhi?.toFixed?.(3) ?? '—'}`} />
        <StatCard label="Committee priced" value={`${t.valued_isins || 0} / ${t.holding_count || 0}`}
          hint="Holdings with a recent market price" icon={ShieldCheck} />
      </div>

      {/* Verdict mix */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Committee verdict mix</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-3 sm:grid-cols-7">
          {['BUY', 'ADD', 'HOLD', 'TRIM', 'SELL', 'PASS', 'no_verdict'].map((k) => {
            const accent = {
              BUY: 'text-emerald-600 dark:text-emerald-400',
              ADD: 'text-emerald-600 dark:text-emerald-400',
              HOLD: 'text-slate-600 dark:text-slate-300',
              TRIM: 'text-amber-600 dark:text-amber-400',
              SELL: 'text-rose-600 dark:text-rose-400',
              PASS: 'text-slate-500 dark:text-slate-400',
              no_verdict: 'text-muted-foreground',
            }[k];
            return (
              <div key={k} className="rounded border bg-muted/40 p-3 text-center">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  {k === 'no_verdict' ? 'No verdict' : k}
                </div>
                <div className={`mt-1 text-2xl font-bold text-foreground ${accent}`}>
                  {v[k] ?? 0}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Sector + size grids */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Sector breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data.sector_breakdown || []).slice(0, 12).map((row) => (
              <div key={row.sector}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="text-foreground">{row.sector}</span>
                  <span className="text-muted-foreground">
                    {fmtPct(row.weight_pct)} · {row.isin_count} {row.isin_count === 1 ? 'name' : 'names'}
                  </span>
                </div>
                <HorizontalBar pct={(row.weight_pct / sectorMax) * 100} />
              </div>
            ))}
            {(!data.sector_breakdown || data.sector_breakdown.length === 0) && (
              <div className="text-sm text-muted-foreground">No holdings yet.</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Market-cap split</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              ['Large cap', s.large_pct, 'bg-blue-500'],
              ['Mid cap', s.mid_pct, 'bg-emerald-500'],
              ['Small cap', s.small_pct, 'bg-amber-500'],
              ['Micro cap', s.micro_pct, 'bg-rose-500'],
              ['Unclassified', s.unknown_pct, 'bg-slate-400'],
            ].map(([label, val, color]) => (
              <div key={label}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span>{label}</span>
                  <span className="text-muted-foreground">{fmtPct(val)}</span>
                </div>
                <HorizontalBar pct={val} color={color} />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Disagreement */}
      {disagreement.length > 0 && (
        <Card className="border-amber-300">
          <CardHeader className="bg-amber-50">
            <CardTitle className="text-base text-amber-900">
              Committee wants action on {disagreement.length} held position(s)
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y">
            {disagreement.map((row) => (
              <div key={row.isin} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-semibold">{row.company_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {row.nse_symbol || row.isin} · current weight {fmtPct(row.weight_pct)}
                  </div>
                  <div className="mt-1 text-sm">{row.one_line_thesis || '—'}</div>
                </div>
                <div className="flex items-center gap-3">
                  <VerdictBadge verdict={row.verdict} />
                  <span className="text-xs text-muted-foreground">conv {row.conviction ?? '—'}</span>
                  <Button size="sm" variant="outline"
                    onClick={() => downloadTicket(row.isin, row.company_name)}
                    disabled={busyTicket === row.isin}>
                    {busyTicket === row.isin
                      ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      : <Download className="mr-1 h-4 w-4" />}
                    Trade ticket
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Full matrix */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Holdings × committee verdicts</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Sector</th>
                <th className="py-2 pr-3 text-right">Weight</th>
                <th className="py-2 pr-3 text-right">Mark value</th>
                <th className="py-2 pr-3">Cap</th>
                <th className="py-2 pr-3">Verdict</th>
                <th className="py-2 pr-3">Conv</th>
                <th className="py-2 pr-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {holdings.map((h) => (
                <tr key={h.isin} className="border-b align-top">
                  <td className="py-2 pr-3">
                    <div className="font-semibold">{h.company_name}</div>
                    <div className="text-[11px] text-muted-foreground">{h.nse_symbol || h.isin}</div>
                  </td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{h.sector}</td>
                  <td className="py-2 pr-3 text-right font-mono">{fmtPct(h.weight_pct)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{fmtINR(h.mark_value_inr)}</td>
                  <td className="py-2 pr-3">
                    <Badge variant="outline" className="text-[10px] uppercase">{h.size_bucket}</Badge>
                  </td>
                  <td className="py-2 pr-3"><VerdictBadge verdict={h.verdict} /></td>
                  <td className="py-2 pr-3 text-center">{h.conviction ?? '—'}</td>
                  <td className="py-2 pr-3">
                    <Button size="sm" variant="ghost"
                      onClick={() => downloadTicket(h.isin, h.company_name)}
                      disabled={busyTicket === h.isin || !h.verdict}>
                      {busyTicket === h.isin
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Download className="h-4 w-4" />}
                    </Button>
                  </td>
                </tr>
              ))}
              {holdings.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                    No holdings in any active book yet. Add a client book to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
